import type { TaskStatus } from "@api/types";
import { ApiError, handleApiError } from "@api/utils";
import { useAuthStore } from "@lib/auth";
import { formatDate, getCustomerName } from "@lib/utils";
import useMatchMutate from "@ui/hooks/match-mutate";
import {
  AlarmClock,
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronRight,
  History,
  Link as LinkIcon,
  Loader2,
  MessageSquare,
  MoreHorizontal,
  Trash2,
  X,
} from "lucide-react";
import Link from "next/link";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useOrgMembers } from "@/api/org-member";
import { taskService, useTask } from "@/api/task";
import { useConfirmDialog } from "@/components/confirm-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TagField } from "@/components/ui/form/tag-field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { TiptapEditor } from "@/components/ui/tiptap-editor";
import { cn } from "@/lib/utils";
import { CustomerAvatar } from "@/modules/customer/customer-avatar";
import { CommentsThread } from "@/modules/comment/comments-thread";
import { trackTaskDeleted, trackTaskUpdated } from "./analytics";
import { AssigneePicker } from "./assignee-picker";
import { DueDatePicker } from "./due-date-picker";
import {
  descriptionSnippet,
  getUserName,
  optimisticTaskPatch,
  type PopulatedTask,
  TASK_STATUS_LABELS,
  TaskUserAvatar,
} from "./shared";
import { SnoozeControl } from "./snooze";
import { TaskHistory } from "./task-history";

interface TaskDetailPanelProps {
  taskId?: string;
  /** Renders a close button and enables Escape-to-close when provided. */
  onClose?: () => void;
  /**
   * Dedicated-page mode (mobile): renders a back arrow top-left instead of
   * the drawer's close button, matching the other mobile detail pages.
   */
  onBack?: () => void;
  /** Called after the task is deleted so the host can dismiss the panel. */
  onDeleted?: () => void;
  /**
   * Embedded mode (contact sidebar stacked view): no panel header chrome —
   * the host provides context/back — just a slim actions row.
   */
  embedded?: boolean;
  className?: string;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="shrink-0 text-sm text-muted-foreground">{label}</span>
      {/* Fixed control column that can still shrink below 190px when the
          panel is embedded in a narrow container (contact sidebar). */}
      <div className="flex w-[190px] min-w-0 justify-end">{children}</div>
    </div>
  );
}

type SaveState = "idle" | "unsaved" | "saving" | "saved";

/**
 * Autosave status for the panel header. Edits save automatically (on blur /
 * on change), so this is the only feedback that a change is pending, in
 * flight, or safely stored.
 */
function SaveIndicator({ state }: { state: SaveState }) {
  if (state === "idle") return null;
  return (
    <span
      aria-live="polite"
      className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"
    >
      {state === "saving" ? (
        <Loader2 className="size-3 animate-spin" aria-hidden="true" />
      ) : state === "saved" ? (
        <Check className="size-3 text-green-500 dark:text-green-400" aria-hidden="true" />
      ) : null}
      {state === "saving" ? "Saving..." : state === "saved" ? "Saved" : "Unsaved"}
    </span>
  );
}

/**
 * Task detail drawer. Rendered inline beside the tasks list (Calls-style
 * side panel) or embedded inside the contact sidebar's view stack.
 */
export function TaskDetailPanel({
  taskId,
  onClose,
  onBack,
  onDeleted,
  embedded,
  className,
}: TaskDetailPanelProps) {
  const org = useAuthStore((state) => state.org);
  const matchMutate = useMatchMutate();
  const { confirm, dialog } = useConfirmDialog();

  const { data, mutate: mutateTask } = useTask(taskId, { populate: "customer user" });
  const task = data as unknown as PopulatedTask | undefined;
  const { data: members } = useOrgMembers(org?.id ?? "", { limit: 100 }, { skip: !org?.id });

  const [view, setView] = useState<"details" | "history">("details");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  // Autosave feedback: in-flight request count drives "saving"; a resolved
  // save shows "saved" for a moment, then the indicator disappears.
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");
  const pendingSavesRef = useRef(0);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  // Re-seed local editing state when a different task loads (or its data
  // refreshes from a realtime update elsewhere).
  useEffect(() => {
    setTitle(task?.title ?? "");
    setDescription(task?.description ?? "");
  }, [task?.id, task?.title, task?.description]);

  useEffect(() => {
    setView("details");
  }, [taskId]);

  useEffect(() => {
    if (!onClose) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose?.();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  async function updateTask(body: Record<string, any>) {
    if (!task) return;
    trackTaskUpdated(task.gym, body, embedded ? "contact-sidebar" : "task-detail");
    pendingSavesRef.current += 1;
    clearTimeout(savedTimerRef.current);
    setSaveState("saving");
    let failed = false;
    // Patch the drawer's own cache first so every control reflects the change
    // instantly; the revalidation afterwards reconciles with the server.
    const patch = optimisticTaskPatch(body, members);
    await mutateTask(
      (current) => (current ? ({ ...current, ...patch } as typeof current) : current),
      { revalidate: false },
    );
    try {
      await taskService.readAndUpdate(task.id, body);
    } catch (err) {
      failed = true;
      if (err instanceof ApiError) handleApiError(err);
    }
    await matchMutate(/\/tasks/);
    pendingSavesRef.current -= 1;
    if (pendingSavesRef.current === 0) {
      setSaveState(failed ? "idle" : "saved");
      if (!failed) {
        savedTimerRef.current = setTimeout(() => setSaveState("idle"), 2000);
      }
    }
  }

  function handleStatusChange(status: TaskStatus) {
    if (status === task?.status) return;
    void updateTask({ status });
  }

  async function handleCopyLink() {
    if (!task) return;
    await navigator.clipboard.writeText(`${window.location.origin}/tasks?task=${task.id}`);
    toast.success("Link copied");
  }

  async function handleDelete() {
    if (!task) return;
    const confirmed = await confirm({
      title: "Delete task?",
      description: `This will permanently delete "${task.title}" along with its comments and history. This action cannot be undone.`,
      confirmLabel: "Delete task",
      cancelLabel: "Cancel",
      variant: "destructive",
    });
    if (!confirmed) return;
    try {
      await taskService.readAndDelete(task.id);
      await matchMutate(/\/tasks/);
      trackTaskDeleted(task.gym, embedded ? "contact-sidebar" : "task-detail");
      toast.success("Task deleted");
      onDeleted?.();
    } catch (err) {
      if (err instanceof ApiError) handleApiError(err);
    }
  }

  const completed = task?.status === "completed";
  const snoozed = task?.status === "snoozed";

  // Mirrors the blur-commit logic: an edit that would produce a save shows as
  // "unsaved" until focus leaves the field and the request fires.
  const normalizedDescription = descriptionSnippet(description) ? description : "";
  const hasUnsavedEdits =
    !!task &&
    ((!!title.trim() && title.trim() !== task.title) ||
      normalizedDescription !== (task.description ?? ""));
  const displaySaveState: SaveState =
    saveState === "saving" ? "saving" : hasUnsavedEdits ? "unsaved" : saveState;

  const actionsMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="xs" className="shadow-none" aria-label="Actions">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onSelect={() => setView("history")} className="gap-2">
          <History className="size-3.5" />
          Task history
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleCopyLink} className="gap-2">
          <LinkIcon className="size-3.5" />
          Copy link
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={handleDelete}
          className="group gap-2 hover:text-destructive focus:text-destructive"
        >
          <Trash2 className="size-3.5 transition-colors group-hover:text-destructive group-focus:text-destructive" />
          Delete task
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const body = !task ? (
    <div className="flex flex-1 items-center justify-center p-6">
      <p className="text-sm text-muted-foreground">Loading task...</p>
    </div>
  ) : view === "history" ? (
    <div className="flex-1 py-4">
      {embedded && (
        <Button
          variant="ghost"
          size="xs"
          className="mb-3 gap-1.5 shadow-none"
          onClick={() => setView("details")}
        >
          <ArrowLeft className="size-3.5" />
          Back to task
        </Button>
      )}
      <TaskHistory taskId={task.id} members={members} />
    </div>
  ) : (
    <div className={cn("flex flex-1 flex-col gap-4", embedded ? "pb-2 pt-1" : "py-4")}>
      <div>
        {/* In embedded mode the save indicator + actions share the title row
            so the description editor below keeps the full container width. */}
        <div className={cn(embedded && "flex items-center gap-2")}>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const next = title.trim();
              if (next && next !== task.title) void updateTask({ title: next });
              else setTitle(task.title);
            }}
            maxLength={500}
            aria-label="Task title"
            placeholder="Task title"
            className="-mx-1.5 w-full min-w-0 flex-1 rounded-md border-0 bg-transparent px-1.5 py-0.5 text-lg font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground hover:bg-muted/50 focus:bg-muted/50 focus:ring-0"
          />
          {embedded && (
            <div className="flex shrink-0 items-center gap-2">
              <SaveIndicator state={displaySaveState} />
              {actionsMenu}
            </div>
          )}
        </div>
        {/* Rich text (same editor as the email builder's text content).
            `onUpdate` only fires on user edits, so an untouched editor never
            writes back the tiptap-normalized HTML of a legacy plain-text
            description. Committing waits until focus leaves the editor
            (toolbar included) to avoid a request per keystroke. */}
        <div
          className="mt-2"
          aria-label="Task description"
          onBlur={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
            const next = descriptionSnippet(description) ? description : "";
            if (next !== (task.description ?? "")) {
              void updateTask({ description: next || null });
            }
          }}
        >
          <TiptapEditor
            content={description}
            onUpdate={setDescription}
            placeholder="Add a description..."
            editorClassName="min-h-[60px]"
          />
        </div>
      </div>

      <div className="flex flex-col divide-y divide-border/50 rounded-md border border-border bg-card px-3 py-1">
        <DetailRow label="Status">
          <Select value={task.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="h-8 w-full text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TASK_STATUS_LABELS)
                // Snoozing goes through the snooze control (it needs a wake
                // date); the option only appears so a snoozed value renders.
                .filter(([value]) => value !== "snoozed" || snoozed)
                .map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </DetailRow>
        {snoozed && (
          <DetailRow label="Snoozed until">
            <SnoozeControl
              snoozedUntil={task.snoozedUntil}
              onSnooze={(until) => void updateTask({ status: "snoozed", snoozedUntil: until })}
              onUnsnooze={() => void updateTask({ status: "todo" })}
              trigger={
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-full justify-start gap-2 font-normal shadow-none"
                >
                  <AlarmClock className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {task.snoozedUntil
                      ? formatDate(task.snoozedUntil, "MMM d, h:mm a")
                      : "Pick a date"}
                  </span>
                </Button>
              }
            />
          </DetailRow>
        )}
        <DetailRow label="Assignee">
          <AssigneePicker
            value={task.user?.id ?? null}
            members={members}
            onChange={(userId) => void updateTask({ user: userId })}
          >
            <Button variant="outline" size="sm" className="h-8 w-full justify-start gap-2 font-normal shadow-none">
              {task.user ? (
                <>
                  <TaskUserAvatar user={task.user} />
                  <span className="truncate">{getUserName(task.user)}</span>
                </>
              ) : (
                <>
                  <TaskUserAvatar user={null} className="opacity-50" />
                  <span className="text-muted-foreground">Assign to...</span>
                </>
              )}
            </Button>
          </AssigneePicker>
        </DetailRow>
        <DetailRow label="Due date">
          <DueDatePicker
            value={task.dueDate ? new Date(task.dueDate) : null}
            onChange={(date) => void updateTask({ dueDate: date })}
            align="end"
            className="h-8 w-full"
          />
        </DetailRow>
        {/* When embedded in the contact sidebar the contact is already the
            surrounding context; elsewhere the contact lives in the header. */}
        {task.thread ? (
          <DetailRow label="Conversation">
            <Link
              href={`/messages/${task.thread}`}
              className="flex items-center gap-1.5 text-sm hover:underline"
            >
              <MessageSquare className="size-4 text-muted-foreground" />
              View conversation
            </Link>
          </DetailRow>
        ) : null}
      </div>

      {/* flex-wrap lets the snooze button drop to its own full-width row in
          narrow containers instead of squeezing the labels onto two lines. */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="min-w-44 flex-1 gap-2 whitespace-nowrap shadow-none"
          onClick={() => void updateTask({ status: completed ? "todo" : "completed" })}
        >
          <CheckCircle2
            className="size-4 shrink-0 text-green-500 dark:text-green-400"
          />
          {completed ? "Reopen task" : "Mark as completed"}
        </Button>
        {!completed && (
          <SnoozeControl
            snoozedUntil={snoozed ? task.snoozedUntil : undefined}
            onSnooze={(until) => void updateTask({ status: "snoozed", snoozedUntil: until })}
            onUnsnooze={() => void updateTask({ status: "todo" })}
            trigger={
              <Button variant="outline" className="flex-1 gap-2 whitespace-nowrap shadow-none">
                <AlarmClock className="size-4 shrink-0 text-muted-foreground" />
                {snoozed ? "Snoozed" : "Snooze"}
              </Button>
            }
          />
        )}
      </div>

      <TagField
        name="tags"
        label="Tags"
        placeholder="Add tags..."
        value={task.tags.join("|")}
        onValueChange={(value) =>
          void updateTask({ tags: value ? value.split("|").filter(Boolean) : [] })
        }
      />

      <Separator />

      <div>
        <h3 className="mb-3 text-sm font-medium text-foreground">Comments</h3>
        <CommentsThread gym={task.gym} task={task.id} members={members} />
      </div>
    </div>
  );

  if (embedded) {
    return (
      <div className={cn("flex w-full flex-col", className)}>
        {body}
        {dialog}
      </div>
    );
  }

  return (
    <div className={cn("flex h-full w-full flex-col overflow-hidden", className)}>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {onBack && view !== "history" && (
            <Button
              variant="ghost"
              size="sm"
              aria-label="Back to tasks"
              className="-ml-1.5 shrink-0 shadow-none"
              onClick={onBack}
            >
              <ArrowLeft className="size-4 shrink-0" />
            </Button>
          )}
          {view === "history" ? (
            <>
              <Button
                variant="ghost"
                size="xs"
                className="shadow-none"
                onClick={() => setView("details")}
              >
                <ArrowLeft className="size-4" />
              </Button>
              <span className="text-sm font-medium">Task history</span>
            </>
          ) : task?.customer ? (
            // Same contact pill as the conversation thread header: the
            // task's contact, one click from their profile.
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="h-8 min-w-0 max-w-full items-center rounded-full border border-transparent px-1.5 font-semibold transition-colors hover:border-border hover:bg-secondary hover:shadow-sm"
            >
              <Link href={`/contacts/${task.customer.id}`}>
                <CustomerAvatar
                  customer={task.customer}
                  className="mr-1.5 size-6 shrink-0 text-[10px]"
                />
                <span className="truncate">{getCustomerName(task.customer)}</span>
                <ChevronRight className="ml-1 size-3 shrink-0 text-muted-foreground" />
              </Link>
            </Button>
          ) : (
            <span className="text-sm font-medium">Task</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {view === "details" && <SaveIndicator state={displaySaveState} />}
          {view === "details" && actionsMenu}
          {onClose && (
            <Button
              variant="ghost"
              size="xs"
              className="shadow-none"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto px-4">{body}</div>
      {dialog}
    </div>
  );
}
